import { nanoid } from 'nanoid';
import {
  AddLineItemHelper, AddLineItemRequestData, Orchestration as OrchestrationPayload,
} from '@printdeal/checkout-shared';
import { IAlternativeUpload } from '@printdeal/alternative-upload';
import type { DocumentReference } from '../types/file-delivery/FileDelivery';
import { GroupSelection } from '../types/selector/Selector';

const buildOrchestrationPayload = (
  orchestrationId?: string,
): OrchestrationPayload | undefined => {
  if (orchestrationId) {
    return {
      id: orchestrationId,
      recipient: 'fulfiller',
      groupId: nanoid(),
    };
  }

  return undefined;
};

export const buildLineItemsFromProduct = (props: {
  /**
   * This is the contentfulPageId from the product page
   */
  contentfulPageId: string;
  /**
   * This is the productKey for the product from cimcommerce
   */
  productKey: string;
  /**
   * This is the variant sku from the line item (lineItem.variant.sku) and is the variant of the product in cimcommerce.
   */
  variantSku?: string;
  /**
   * This is the version of the product from PIM.
   */
  productVersion?: number;
  userSelection: Record<string, string>;
  groupSelection?: GroupSelection[];
  documentReferences?: DocumentReference[];
  alternativelyUploadedFiles?: IAlternativeUpload[];
  editorType?: string;
  services?: string[];
  orchestrationId?: string;
  deliveryOptionId?: string;
}): AddLineItemRequestData => {
  const {
    contentfulPageId,
    productKey,
    productVersion,
    variantSku,
    userSelection,
    groupSelection,
    documentReferences,
    alternativelyUploadedFiles,
    editorType,
    services,
    orchestrationId,
    deliveryOptionId,
  } = props;

  const userUploadsUrl = alternativelyUploadedFiles && alternativelyUploadedFiles
    .map(({
      id, uploadTitle, urls, uploadTimestamp,
    }) => ([id, uploadTitle, urls?.originalUrl, uploadTimestamp].join('::')));

  // If Textile product (groupSelection is used for the quantity grid)
  if (groupSelection && groupSelection.length) {
    // When there is a group selection, we need to add the group selection to the user selection
    const groupUserSelections = groupSelection.map(({ attributes, quantity }): Record<string, string> => ({
      ...attributes,
      ...userSelection,
      Quantity: quantity.toString(),
    }));

    // Create a groupId for the selection
    const groupId = nanoid();

    // Create a line item for each selection
    const lineItems = groupUserSelections.map((selection, index) => AddLineItemHelper.buildLineItemShape({
      productId: contentfulPageId,
      productKey,
      productVersion,
      userSelection: selection,
      variantSku,
      quantity: Number(selection.Quantity),
      groupId,
      fulfillmentGroupId: groupId,
      isGroupLead: index === 0,
      documentReferenceUrl: documentReferences?.[0]?.documentReferenceUrl,
      userUploadsUrl,
      editorType,
      services: services?.length && index === 0 ? services : undefined,
      deliveryOptionId,
    }));

    return {
      lineItems,
    };
  }

  // Multiple designs (if there is more than one document reference)
  if (documentReferences && documentReferences.length > 1) {
    const multipleDesignsGroup = nanoid(6);
    const lineItems = documentReferences.map(({ documentReferenceUrl, quantity }) =>
      AddLineItemHelper.buildLineItemShape({
        productId: contentfulPageId,
        productKey,
        productVersion,
        variantSku,
        userSelection,
        documentReferenceUrl,
        quantity,
        userUploadsUrl,
        editorType,
        services,
        deliveryOptionId,
        multipleDesignsGroup,
      }));

    return {
      lineItems,
    };
  }

  // When there is no group selection, just create a single line item shape with the user selection
  const lineItem = AddLineItemHelper.buildLineItemShape({
    productId: contentfulPageId,
    productKey,
    productVersion,
    userSelection,
    variantSku,
    documentReferenceUrl: documentReferences?.[0]?.documentReferenceUrl,
    userUploadsUrl,
    editorType,
    orchestration: buildOrchestrationPayload(orchestrationId),
    services,
    deliveryOptionId,
  });

  return {
    lineItems: [lineItem],
  };
};
